import type { UserRole } from '@doneverse/shared'

/**
 * Boundary-aware route match: '/teams' matches '/teams' and '/teams/1', not
 * '/teams-x'.
 *
 * Lives here rather than beside any one consumer because "is this pathname on
 * or under this route" is the shared rule behind every access decision in the
 * app — the nav's active state, the restricted-doer allowlist, the flag route
 * guard and the onboarding lock all ask it, and they must all answer it the
 * same way.
 */
export function matchesPathPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * Roles admitted to the app shell (`_appLayout` in router.tsx), the parent of
 * every founder, doer, team and admin page, and of /notifications. Support
 * staff are left out on purpose: that is what fences them to Settings and
 * Feedback (ClickUp 14zf25znmg1). A supervisor is in, as a DSL is, because
 * their team's Activity Log lives here; every page in the shell other than
 * /notifications and /notifications/devices has its own requireRole, so that
 * admits them to nothing else (ClickUp 14zf25znmg4). Read this instead of restating the list, so a link
 * into the shell can be hidden from exactly the roles the router would bounce.
 */
export const APP_SHELL_ROLES: UserRole[] = [
  'founder',
  'doer',
  'doing_team',
  'intake_team',
  'admin',
  'escalation_officer',
  'dsl',
  'supervisor',
]

/**
 * Roles the API scopes to the team they own (`podKindsForRole` in the API's
 * `lib/escalation-scope.ts`): a `dsl` reads their dsl_team plus any
 * support_team, a `supervisor` their support_team. On the Activity Log and
 * Activity Summary they share one behaviour: the doer picker comes from
 * `/v1/me/team` instead of the admin user search, there is no Export, and they
 * get full detail (ClickUp 14zf25znmg4).
 */
export const TEAM_SCOPED_VIEWER_ROLES: UserRole[] = ['dsl', 'supervisor']

export function isTeamScopedViewer(role: UserRole | undefined): boolean {
  return role !== undefined && TEAM_SCOPED_VIEWER_ROLES.includes(role)
}

/**
 * Centralized home page routing by role.
 * Update this single location to change where users land after login.
 */
export function getHomeRoute(role: UserRole | undefined): string {
  switch (role) {
    case 'admin':
      return '/admin/stats'
    case 'founder':
      return '/start-here'
    case 'doer':
      return '/doer-home'
    case 'doing_team':
      return '/projects'
    case 'intake_team':
      return '/intake'
    case 'escalation_officer':
      return '/escalations'
    case 'dsl':
    case 'supervisor':
      return '/admin/activity-log'
    // Support staff do not ride the app shell (_appLayout): they only install
    // DoneFlow and read their own activity. Their home is in Settings, the one
    // layout they may reach, so requireRole's redirect lands somewhere it will
    // not bounce from (ClickUp 14zf25znmg1).
    case 'support_staff':
      return '/settings/activity-agent'
    default:
      return '/doing-blueprint'
  }
}
